import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useNavigate } from 'react-router-dom'
import * as casesApi from '../../services/cases'
import type {
  ClientProfileWithCaseCount,
  CreateClientProfileInput,
  DeleteClientOptions,
  UpdateClientProfileInput,
} from '../../types/case-management'
import { EMPTY_FORM_DATA, type DeleteOption } from './types'

interface ModalApi {
  setShowCreateModal: (value: boolean) => void
  setShowEditModal: (value: boolean) => void
  setShowDeleteModal: (value: boolean) => void
  setShowLinkCaseModal: (value: boolean) => void
  setSelectedClient: (value: ClientProfileWithCaseCount | null) => void
  setActionMenuClient: (value: string | null) => void
  selectedClient: ClientProfileWithCaseCount | null
}

interface FormApi {
  formData: CreateClientProfileInput
  setFormData: (data: CreateClientProfileInput) => void
  setFormLoading: (loading: boolean) => void
  setFormError: (error: string | null) => void
}

interface DelApi {
  deleteOption: DeleteOption
  setDeleteOption: (option: DeleteOption) => void
  reassignTargetId: string
  setReassignTargetId: (id: string) => void
}

interface LinkApi {
  selectedCaseToLink: string
  setSelectedCaseToLink: (id: string) => void
  setUnlinkedCases: (cases: import('../../types/case-management').Case[]) => void
  setUnlinkedCasesLoading: (loading: boolean) => void
}

interface MutationsApi {
  create: (input: CreateClientProfileInput) => Promise<unknown>
  update: (id: string, input: UpdateClientProfileInput) => Promise<unknown>
  remove: (id: string, options: DeleteClientOptions) => Promise<boolean>
  mutationError: string | null
  refetch: () => Promise<unknown>
}

export interface ClientsActionsInput {
  isAdmin: boolean
  modal: ModalApi
  form: FormApi
  del: DelApi
  link: LinkApi
  mutations: MutationsApi
}

function buildUpdateData(
  formData: CreateClientProfileInput,
  selected: ClientProfileWithCaseCount,
  isAdmin: boolean
): UpdateClientProfileInput {
  const update: UpdateClientProfileInput = {}
  if (formData.shortName) update.shortName = formData.shortName
  if (formData.industry) update.industry = formData.industry
  if (formData.referenceCode) update.referenceCode = formData.referenceCode
  if (formData.notes) update.notes = formData.notes
  if (formData.primaryContactName) update.primaryContactName = formData.primaryContactName
  if (formData.primaryContactEmail) update.primaryContactEmail = formData.primaryContactEmail
  if (formData.primaryContactPhone) update.primaryContactPhone = formData.primaryContactPhone
  if (isAdmin && formData.name !== selected.name) update.name = formData.name
  return update
}

function buildDeleteOptions(
  selected: ClientProfileWithCaseCount,
  deleteOption: DeleteOption,
  reassignTargetId: string
): DeleteClientOptions {
  const options: DeleteClientOptions = {}
  if (selected.caseCount > 0) {
    if (deleteOption === 'reassign' && reassignTargetId) {
      options.reassignTo = reassignTargetId
    } else {
      options.unlinkCases = true
    }
  }
  return options
}

function clientToFormData(client: ClientProfileWithCaseCount): CreateClientProfileInput {
  return {
    name: client.name,
    shortName: client.shortName ?? '',
    industry: client.industry ?? '',
    referenceCode: client.referenceCode ?? '',
    notes: client.notes ?? '',
    primaryContactName: client.primaryContactName ?? '',
    primaryContactEmail: client.primaryContactEmail ?? '',
    primaryContactPhone: client.primaryContactPhone ?? '',
  }
}

export interface ClientsActions {
  handleCreate: (event: React.FormEvent) => Promise<void>
  handleUpdate: (event: React.FormEvent) => Promise<void>
  handleDelete: () => Promise<void>
  handleLinkCase: () => Promise<void>
  handleViewClientCases: (clientId: string) => void
  openEditModal: (client: ClientProfileWithCaseCount) => void
  openDeleteModal: (client: ClientProfileWithCaseCount) => void
  openLinkCaseModal: (client: ClientProfileWithCaseCount) => Promise<void>
  openCreateModal: () => void
  closeFormModal: () => void
  closeDeleteModal: () => void
  closeLinkModal: () => void
}

interface SubmitActions {
  handleCreate: (event: React.FormEvent) => Promise<void>
  handleUpdate: (event: React.FormEvent) => Promise<void>
  handleDelete: () => Promise<void>
}

function buildSubmitActions(input: ClientsActionsInput, t: TFunction<'cases'>): SubmitActions {
  const { isAdmin, modal, form, del, mutations } = input
  const handleCreate = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    form.setFormLoading(true)
    form.setFormError(null)
    const result = await mutations.create(form.formData)
    if (result) {
      modal.setShowCreateModal(false)
      form.setFormData(EMPTY_FORM_DATA)
      void mutations.refetch()
    } else {
      form.setFormError(mutations.mutationError ?? t('clients.failedCreate'))
    }
    form.setFormLoading(false)
  }
  const handleUpdate = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    if (!modal.selectedClient) return
    form.setFormLoading(true)
    form.setFormError(null)
    const data = buildUpdateData(form.formData, modal.selectedClient, isAdmin)
    const result = await mutations.update(modal.selectedClient.id, data)
    if (result) {
      modal.setShowEditModal(false)
      modal.setSelectedClient(null)
      void mutations.refetch()
    } else {
      form.setFormError(mutations.mutationError ?? t('clients.failedUpdate'))
    }
    form.setFormLoading(false)
  }
  const handleDelete = async (): Promise<void> => {
    if (!modal.selectedClient) return
    form.setFormLoading(true)
    form.setFormError(null)
    const options = buildDeleteOptions(modal.selectedClient, del.deleteOption, del.reassignTargetId)
    const success = await mutations.remove(modal.selectedClient.id, options)
    if (success) {
      modal.setShowDeleteModal(false)
      modal.setSelectedClient(null)
      del.setDeleteOption('unlink')
      del.setReassignTargetId('')
      void mutations.refetch()
    } else {
      form.setFormError(mutations.mutationError ?? t('clients.failedDelete'))
    }
    form.setFormLoading(false)
  }
  return { handleCreate, handleUpdate, handleDelete }
}

interface LinkActions {
  openLinkCaseModal: (client: ClientProfileWithCaseCount) => Promise<void>
  handleLinkCase: () => Promise<void>
}

function buildLinkActions(input: ClientsActionsInput, t: TFunction<'cases'>): LinkActions {
  const { modal, form, link, mutations } = input
  const openLinkCaseModal = async (client: ClientProfileWithCaseCount): Promise<void> => {
    modal.setSelectedClient(client)
    modal.setActionMenuClient(null)
    modal.setShowLinkCaseModal(true)
    link.setUnlinkedCasesLoading(true)
    link.setSelectedCaseToLink('')
    form.setFormError(null)
    try {
      const allCases = await casesApi.listCases()
      link.setUnlinkedCases(allCases.filter((caseItem) => !caseItem.clientProfileId))
    } catch {
      form.setFormError(t('clients.failedLoadCases'))
    } finally {
      link.setUnlinkedCasesLoading(false)
    }
  }
  const handleLinkCase = async (): Promise<void> => {
    if (!modal.selectedClient || !link.selectedCaseToLink) return
    form.setFormLoading(true)
    form.setFormError(null)
    try {
      await casesApi.updateCase(link.selectedCaseToLink, {
        clientProfileId: modal.selectedClient.id,
      })
      modal.setShowLinkCaseModal(false)
      modal.setSelectedClient(null)
      link.setSelectedCaseToLink('')
      void mutations.refetch()
    } catch {
      form.setFormError(t('clients.failedLinkCase'))
    } finally {
      form.setFormLoading(false)
    }
  }
  return { openLinkCaseModal, handleLinkCase }
}

interface OpenCloseActions {
  openEditModal: (client: ClientProfileWithCaseCount) => void
  openDeleteModal: (client: ClientProfileWithCaseCount) => void
  openCreateModal: () => void
  closeFormModal: () => void
  closeDeleteModal: () => void
  closeLinkModal: () => void
}

function buildOpenCloseActions(input: ClientsActionsInput): OpenCloseActions {
  const { modal, form, del } = input
  const openEditModal = (client: ClientProfileWithCaseCount): void => {
    modal.setSelectedClient(client)
    form.setFormData(clientToFormData(client))
    form.setFormError(null)
    modal.setShowEditModal(true)
    modal.setActionMenuClient(null)
  }
  const openDeleteModal = (client: ClientProfileWithCaseCount): void => {
    modal.setSelectedClient(client)
    form.setFormError(null)
    del.setDeleteOption('unlink')
    del.setReassignTargetId('')
    modal.setShowDeleteModal(true)
    modal.setActionMenuClient(null)
  }
  const openCreateModal = (): void => {
    form.setFormData(EMPTY_FORM_DATA)
    form.setFormError(null)
    modal.setShowCreateModal(true)
  }
  const closeFormModal = (): void => {
    modal.setShowCreateModal(false)
    modal.setShowEditModal(false)
    modal.setSelectedClient(null)
  }
  const closeDeleteModal = (): void => {
    modal.setShowDeleteModal(false)
    modal.setSelectedClient(null)
  }
  const closeLinkModal = (): void => {
    modal.setShowLinkCaseModal(false)
    modal.setSelectedClient(null)
  }
  return {
    openEditModal,
    openDeleteModal,
    openCreateModal,
    closeFormModal,
    closeDeleteModal,
    closeLinkModal,
  }
}

export function useClientsActions(input: ClientsActionsInput): ClientsActions {
  const { t } = useTranslation('cases')
  const navigate = useNavigate()
  const submitActions = buildSubmitActions(input, t)
  const linkActions = buildLinkActions(input, t)
  const openCloseActions = buildOpenCloseActions(input)
  const handleViewClientCases = (clientId: string): void => {
    void navigate(`/cases?clientProfileId=${clientId}`)
  }
  return { ...submitActions, ...linkActions, ...openCloseActions, handleViewClientCases }
}
